/**
 * FoldBack in-app feedback.
 *
 * Loads the FoldBack SDK so you can click any element on a page, describe what
 * should change, and have it land in the review queue.
 *
 * The page carries a PUBLISHABLE key (`fb_pub_*`) — project-scoped,
 * origin-bound, safe to ship. Never an `fb_cli_*` session token: one of those
 * in a shipped page is an owner-level credential for the whole account, which
 * is exactly what this file replaced.
 *
 * Off by default. It loads only when the user turns it on in
 * Settings → Feedback, or when the URL carries ?foldback=enabled — so ordinary
 * visitors never see the overlay.
 */

const STORAGE_KEY = 'sparkforge.foldback.enabled';

const CONFIG = {
  server: process.env.VUE_APP_FOLDBACK_URL,
  project: process.env.VUE_APP_FOLDBACK_PROJECT,
  key: process.env.VUE_APP_FOLDBACK_KEY
};

let loaded = false;

/**
 * Has the user switched feedback on for this browser?
 *
 * Defaults to ON. This is a prototyping tool for its own designers, not a
 * public product surface — the useful default is that feedback is there unless
 * someone turns it off.
 */
export function isFeedbackEnabled() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  } catch (e) {
    // Private windows and blocked site-data throw on access rather than
    // returning null, so a bare read would break the settings page.
    return true;
  }
}

export function setFeedbackEnabled(on) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'true' : 'false');
  } catch (e) {
    /* nothing to do — the toggle simply will not persist */
  }
  if (on) {
    loadFoldback();
  }
  return on;
}

/** ?foldback=enabled turns it on for one page load without changing settings. */
function activatedByUrl() {
  try {
    return new URLSearchParams(window.location.search).get('foldback') === 'enabled';
  } catch (e) {
    return false;
  }
}

export function isConfigured() {
  return Boolean(CONFIG.server && CONFIG.project && CONFIG.key);
}

/**
 * Inject the SDK. Safe to call repeatedly — it only ever loads once.
 */
export function loadFoldback() {
  if (loaded || typeof document === 'undefined') return false;
  if (!isConfigured()) {
    console.warn(
      '[foldback] not configured — set VUE_APP_FOLDBACK_URL, _PROJECT and _KEY. ' +
        'Feedback is unavailable until then.'
    );
    return false;
  }

  loaded = true;
  const s = document.createElement('script');
  s.src = `${CONFIG.server}/sdk/foldback-sdk.js`;
  s.async = true;
  s.dataset.server = CONFIG.server;
  s.dataset.project = CONFIG.project;
  s.dataset.key = CONFIG.key;
  // Already gated by the toggle above, so the SDK itself should not also wait
  // for a query string — that combination is what makes an installed SDK look
  // broken.
  s.dataset.activate = 'always';

  // Release context: which build the feedback was left on.
  if (process.env.VUE_APP_COMMIT_SHA) s.dataset.commitSha = process.env.VUE_APP_COMMIT_SHA;
  if (process.env.VUE_APP_BRANCH) s.dataset.branch = process.env.VUE_APP_BRANCH;

  s.onerror = () => {
    loaded = false;
    console.warn(
      `[foldback] SDK failed to load from ${CONFIG.server}. ` +
        'If this is a local build, the FoldBack server may not be running.'
    );
  };
  document.body.appendChild(s);
  return true;
}

/**
 * Ask FoldBack whether feedback is actually switched on for this project.
 *
 * The SDK can load perfectly and still render nothing, because the project
 * carries its own on/off switch server-side. When that is `off` the page is
 * silent and gives no clue why — which is exactly the dead end this reports,
 * so the settings panel can say "FoldBack has feedback turned off for this
 * project" instead of the user toggling a switch that appears to do nothing.
 */
export async function checkFoldbackStatus() {
  if (!isConfigured()) {
    return { ok: false, reason: 'not-configured' };
  }
  try {
    const res = await fetch(
      `${CONFIG.server}/api/sdk/feedback/config?project=${encodeURIComponent(CONFIG.project)}`,
      { headers: { 'X-FoldBack-Key': CONFIG.key } }
    );
    if (!res.ok) return { ok: false, reason: 'unreachable', status: res.status };
    const cfg = await res.json();
    return {
      ok: Boolean(cfg.enabled),
      reason: cfg.enabled ? 'ready' : 'disabled-on-project',
      mode: cfg.mode
    };
  } catch (e) {
    return { ok: false, reason: 'unreachable', error: e.message };
  }
}

/**
 * Call once on app boot. Loads the SDK if the user has opted in, or if the URL
 * asks for it this once.
 */
export function initFoldback() {
  if (isFeedbackEnabled() || activatedByUrl()) {
    return loadFoldback();
  }
  return false;
}

export const foldbackServer = CONFIG.server;
